import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

function TicketTypeSelector({ eventId, ticketTypes, busCapacity, onBuy }) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const busRemaining = busCapacity ? busCapacity.total_seats - busCapacity.sold_seats : 0;

  const getCapacityInfo = (tt) => {
    const remaining = tt.capacity - tt.sold;
    if (tt.includes_bus) {
      return Math.min(remaining, busRemaining);
    }
    return remaining;
  };

  const getTypeColor = (tt) => {
    if (tt.is_backstage) return '#9b59b6';
    if (tt.is_vip) return '#f39c12';
    if (tt.is_free) return '#55cc55';
    return colors.accent;
  };

  const getTypeIcon = (tt) => {
    if (tt.is_backstage) return <StarIcon style={{ fontSize: 16 }} />;
    if (tt.is_vip) return <StarIcon style={{ fontSize: 16 }} />;
    if (tt.is_free) return <CardGiftcardIcon style={{ fontSize: 16 }} />;
    if (tt.includes_bus) return <DirectionsBusIcon style={{ fontSize: 16 }} />;
    if (tt.includes_drink) return <LocalBarIcon style={{ fontSize: 16 }} />;
    return null;
  };

  const getFee = (tt) => {
    if (tt.custom_fee) return parseFloat(tt.custom_fee);
    return parseFloat(tt.price) * 0.10;
  };

  const getTotal = (tt, qty) => {
    return (parseFloat(tt.price) + getFee(tt)) * qty;
  };

  // Lógica de tramos
  const vipAndBackstage = ticketTypes.filter(tt => (tt.is_vip || tt.is_backstage) && tt.sold < tt.capacity);
  const freeTypes = ticketTypes.filter(tt => tt.is_free && tt.sold < tt.capacity);
  const generalTypes = ticketTypes.filter(tt => !tt.is_vip && !tt.is_backstage && !tt.is_free);
  const currentTramoPosition = generalTypes.find(tt => tt.sold < tt.capacity)?.order_position;
  const currentTramo = generalTypes.filter(tt =>
    tt.order_position === currentTramoPosition ||
    (currentTramoPosition && tt.order_position === currentTramoPosition + 1 &&
      tt.includes_bus &&
      !generalTypes.find(t => t.order_position === currentTramoPosition)?.includes_bus)
  ).filter(tt => tt.sold < tt.capacity);

  const availableTypes = [...freeTypes, ...currentTramo, ...vipAndBackstage];

  const handleBuy = () => {
    if (!selected) return;
    const user = localStorage.getItem('user');
    if (!user) {
      navigate('/login');
      return;
    }
    onBuy(selected, quantity);
  };

  if (availableTypes.length === 0) {
    return (
      <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: '1px solid ' + colors.border, padding: '1.5rem', textAlign: 'center' }}>
        <p style={{ color: colors.textSecondary, margin: 0 }}>Entradas agotadas</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginBottom: '1rem' }}>
        {availableTypes.map(tt => {
          const remaining = getCapacityInfo(tt);
          const isAvailable = remaining > 0;
          const isSelected = selected?.id === tt.id;
          const typeColor = getTypeColor(tt);
          const fee = getFee(tt);

          return (
            <div
              key={tt.id}
              onClick={() => isAvailable && setSelected(tt)}
              style={{
                backgroundColor: isSelected ? typeColor + '22' : colors.bgSecondary,
                border: '1px solid ' + (isSelected ? typeColor : colors.border),
                borderRadius: '12px',
                padding: '1rem',
                cursor: isAvailable ? 'pointer' : 'default',
                opacity: isAvailable ? 1 : 0.5,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                    <span style={{ color: typeColor }}>{getTypeIcon(tt)}</span>
                    <span style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.95rem' }}>{tt.name}</span>
                    {!isAvailable && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#ff444422', color: '#ff4444', padding: '2px 8px', borderRadius: '20px' }}>Agotado</span>
                    )}
                  </div>
                  {tt.description && (
                    <p style={{ color: colors.textSecondary, fontSize: '0.82rem', margin: '0 0 0.4rem', lineHeight: 1.4 }}>{tt.description}</p>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {tt.includes_bus && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#1a3a5c', color: '#55bbff', padding: '2px 8px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <DirectionsBusIcon style={{ fontSize: 11 }} /> Autobús
                      </span>
                    )}
                    {tt.includes_drink && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#1a3a1a', color: '#55cc55', padding: '2px 8px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <LocalBarIcon style={{ fontSize: 11 }} /> Copa incluida
                      </span>
                    )}
                    {tt.is_vip && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#3a2a00', color: '#f39c12', padding: '2px 8px', borderRadius: '20px' }}>VIP</span>
                    )}
                    {tt.is_backstage && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#2a0a3a', color: '#9b59b6', padding: '2px 8px', borderRadius: '20px' }}>Backstage</span>
                    )}
                    {tt.is_free && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#0a3a0a', color: '#55cc55', padding: '2px 8px', borderRadius: '20px' }}>GRATIS · 17:30h-18:30h</span>
                    )}
                    {isAvailable && remaining <= 20 && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#ff440022', color: '#ff6644', padding: '2px 8px', borderRadius: '20px' }}>
                        ¡Solo quedan {remaining}!
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                  <p style={{ color: typeColor, fontSize: '1.4rem', fontWeight: 'bold', margin: 0 }}>
                    {tt.is_free ? 'GRATIS' : `${parseFloat(tt.price).toFixed(2)}€`}
                  </p>
                  {!tt.is_free && (
                    <p style={{ color: colors.textSecondary, fontSize: '0.72rem', margin: 0 }}>+ {fee.toFixed(2)}€ gestión</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: '1px solid ' + colors.border, padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <span style={{ color: colors.text, fontSize: '0.9rem' }}>Cantidad:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={{ backgroundColor: colors.bgTertiary, border: '1px solid ' + colors.border, color: colors.text, borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</button>
              <span style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.1rem', minWidth: '20px', textAlign: 'center' }}>{quantity}</span>
              <button onClick={() => setQuantity(q => Math.min(8, q + 1))} style={{ backgroundColor: colors.bgTertiary, border: '1px solid ' + colors.border, color: colors.text, borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <span style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>Total:</span>
            <span style={{ color: colors.accent, fontWeight: 'bold', fontSize: '1.1rem' }}>
              {selected.is_free ? 'GRATIS' : `${getTotal(selected, quantity).toFixed(2)}€`}
            </span>
          </div>

          <button
            onClick={handleBuy}
            style={{ width: '100%', backgroundColor: colors.accent, color: colors.accentText, border: 'none', borderRadius: '25px', padding: '1rem', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {selected.is_free ? 'Conseguir entrada gratis' : 'Comprar entradas'}
          </button>
        </div>
      )}
    </div>
  );
}

export default TicketTypeSelector;